// ST-1705 -Claude- Business portal login page. The route is kept compatible
// with /admin/login while public navigation points businesses to /business/login.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card } from '@nearrme/ui-web';
import { BAG_MARK_SVG } from '@nearrme/brand';
import { getLoginOptions, login, loginBusinessWithCode, requestBusinessLoginCode, setAdminAuth, type LoginOptionsState } from '../api';

export function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from ?? '/admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState<'password' | 'email-code'>('password');
  const [codeRequested, setCodeRequested] = useState(false);
  const [loginOptions, setLoginOptions] = useState<LoginOptionsState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getLoginOptions()
      .then(setLoginOptions)
      .catch((err) => console.warn('[business login options]', err));
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const auth = mode === 'email-code'
        ? await loginBusinessWithCode(email.trim(), code)
        : await login(email.trim(), password);
      setAdminAuth(auth);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      console.error('[business login]', err);
      setError(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async () => {
    setError(null);
    setLoading(true);
    try {
      await requestBusinessLoginCode(email.trim());
      setCodeRequested(true);
    } catch (err: unknown) {
      console.error('[business login code]', err);
      setError(err instanceof Error ? err.message : 'Unable to send login code.');
    } finally {
      setLoading(false);
    }
  };

  const canUsePassword = loginOptions?.emailPasswordEnabled ?? true;
  const canUseEmailCode = loginOptions?.emailCodeEnabled ?? true;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
        <div className="mx-auto flex min-h-16 max-w-7xl items-center justify-between gap-3 px-4">
          <Link to="/" className="inline-flex min-w-0 items-center gap-2">
            <span className="h-9 w-9 shrink-0" dangerouslySetInnerHTML={{ __html: BAG_MARK_SVG }} />
            <span className="truncate text-xl font-light tracking-[0.08em] text-slate-950 dark:text-slate-50 sm:text-2xl">
              Nea<span className="font-normal text-accent-red">r</span><span className="font-normal text-brand-500 dark:text-brand-400">r</span>me
            </span>
          </Link>
          <nav className="hidden items-center gap-1 text-sm sm:flex">
            {[
              { to: '/', label: 'Home', strong: false },
              { to: '/business/signup', label: 'Request Business Access', strong: false },
            { to: '/login', label: 'Consumer login', strong: true },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2 font-medium ${
                  item.strong
                    ? 'text-brand-700 hover:bg-brand-50 hover:text-brand-600 dark:text-brand-300 dark:hover:bg-slate-900 dark:hover:text-brand-200'
                    : 'text-slate-700 hover:bg-slate-100 hover:text-brand-600 dark:text-slate-200 dark:hover:bg-slate-900 dark:hover:text-brand-300'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
        <nav className="grid grid-cols-3 border-t border-slate-100 px-2 py-2 text-center text-sm dark:border-slate-900 sm:hidden">
          <Link to="/" className="rounded-md px-2 py-2 font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-900">
            Home
          </Link>
          <Link to="/business/signup" className="rounded-md px-2 py-2 font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-900">
            Request Access
          </Link>
          <Link to="/login" className="rounded-md px-2 py-2 font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-slate-900">
            Consumer
          </Link>
        </nav>
      </header>

      <main className="flex-1 px-4 py-8 sm:py-12">
      <div className="mx-auto grid w-full max-w-5xl items-stretch gap-8 lg:grid-cols-[minmax(0,1fr)_420px]">
        <section className="hidden min-h-[560px] flex-col justify-between rounded-lg bg-gradient-to-br from-brand-700 via-emerald-700 to-slate-950 p-8 text-white lg:flex">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-100">
              Nearrme business portal
            </p>
            <h1 className="mt-4 text-4xl font-bold tracking-tight">
              Manage verified stores, products, and orders.
            </h1>
            <p className="mt-4 text-lg text-emerald-50">
              Keep your company profile, store listings, product availability, and customer follow-up accurate.
            </p>
          </div>

          <div className="grid gap-3 text-sm font-medium">
            <div className="rounded-lg border border-white/15 bg-white/10 px-4 py-3">
              Review customer interest, Contact Us messages, and follow-up tasks from one place.
            </div>
            <div className="rounded-lg border border-white/15 bg-white/10 px-4 py-3">
              Keep store details, product listings, and inventory visibility ready for nearby buyers.
            </div>
            <div className="rounded-lg border border-white/15 bg-white/10 px-4 py-3">
              Verified stores can show manufacturer warranty-backed product listings.
            </div>
          </div>
        </section>

        <Card padded={true} className="w-full lg:min-h-[560px]">
          <div className="mb-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
              Verified store access
            </p>
            <h1 className="mt-3 text-2xl font-bold text-slate-900 dark:text-slate-100">
              Business login
            </h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Sign in to manage your company, stores, products, orders, and customer messages.
            </p>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1 dark:bg-slate-900">
            <button
              type="button"
              disabled={!canUsePassword}
              onClick={() => setMode('password')}
              className={`rounded-md px-3 py-2 text-sm font-semibold ${mode === 'password' ? 'bg-white text-slate-950 shadow-sm dark:bg-slate-800 dark:text-slate-50' : 'text-slate-600 dark:text-slate-300 disabled:opacity-40'}`}
            >
              Password
            </button>
            <button
              type="button"
              disabled={!canUseEmailCode}
              onClick={() => setMode('email-code')}
              className={`rounded-md px-3 py-2 text-sm font-semibold ${mode === 'email-code' ? 'bg-white text-slate-950 shadow-sm dark:bg-slate-800 dark:text-slate-50' : 'text-slate-600 dark:text-slate-300 disabled:opacity-40'}`}
            >
              Email code
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Business email
              </span>
              <input
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                autoFocus
              />
              <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                Use the email assigned to your company or store profile.
              </span>
            </label>

            {mode === 'password' && (
            <label className="block">
              <span className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Password
              </span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </label>
            )}

            {mode === 'email-code' && (
              <div className="grid gap-3">
                <Button type="button" variant="outline" fullWidth disabled={!email || loading} onClick={handleRequestCode}>
                  {codeRequested ? 'Send code again' : 'Send code to email'}
                </Button>
                {codeRequested && (
                  <label className="block">
                    <span className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Login code
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      required
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </label>
                )}
              </div>
            )}

            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 px-3 py-2 rounded-lg">
                {error}
              </div>
            )}

            <Button type="submit" variant="primary" size="lg" fullWidth loading={loading} disabled={mode === 'email-code' && !codeRequested}>
              {mode === 'email-code' ? 'Sign in with code' : 'Sign in'}
            </Button>
          </form>

          <div className="mt-5 grid gap-2 text-sm text-center">
            <Link to="/forgot-password" className="text-brand-700 dark:text-brand-300 hover:underline font-medium">
              Forgot password?
            </Link>
            <Link to="/business/signup" className="text-slate-600 dark:text-slate-300 hover:text-brand-700">
              Request Business Access
            </Link>
            <Link to="/login" className="text-slate-600 dark:text-slate-300 hover:text-brand-700">
              Consumer login
            </Link>
          </div>

          <p className="mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
            Business access is verified before store management is enabled.
          </p>

        </Card>
      </div>
      </main>
      <footer className="border-t border-slate-200 bg-white px-4 py-6 text-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-[1.5fr_1fr_1fr] md:items-start">
          <div className="text-slate-600 dark:text-slate-300">
            <div className="inline-flex items-center gap-2">
              <span className="h-7 w-7" dangerouslySetInnerHTML={{ __html: BAG_MARK_SVG }} />
              <span className="text-lg font-light tracking-[0.08em] text-slate-950 dark:text-slate-50">
                Nea<span className="font-normal text-accent-red">r</span><span className="font-normal text-brand-500 dark:text-brand-400">r</span>me
              </span>
            </div>
            <p className="mt-3 max-w-xl">
              Verified stores and company teams manage listings, products, orders, and customer follow-up here.
            </p>
          </div>
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Business</h2>
            <div className="mt-3 grid gap-2 text-slate-600 dark:text-slate-300">
              <Link to="/business/signup" className="hover:text-brand-700 dark:hover:text-brand-300">Request Business Access</Link>
              <Link to="/about" className="hover:text-brand-700 dark:hover:text-brand-300">Contact Nearrme</Link>
            </div>
          </div>
          <div className="text-slate-500 dark:text-slate-400 md:text-right">
            <div>Nearrme is a registered trademark.</div>
            <div className="mt-1">Powered by Uniworld Technologies Pvt. Ltd.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
